import { getVendorLogoCollectionByGroup } from "#root/schema/vendor";
import { useData } from "#root/utility/hooks";
import type { ComponentType, ReactNode } from "react";
import {
  TbBolt,
  TbBuildingBank,
  TbChartCandle,
  TbCode,
  TbCurrencyDollar,
  TbDashboard,
  TbDatabase,
  TbDeviceMobile,
  TbFileCode,
  TbMessageChatbot,
  TbPlugConnected,
  TbRobot,
  TbSearch,
  TbServer,
  TbTrendingUp,
  TbWorld,
} from "react-icons/tb";
import { Link } from "react-router-dom";
import PageLayout from "./layout";

export default function HomePage(): ReactNode {
  const [dataSources] = useData(async () => {
    const { rows } = await getVendorLogoCollectionByGroup("dataSource").list();
    return rows;
  });

  const [newsSources] = useData(async () => {
    const { rows } = await getVendorLogoCollectionByGroup("newsSource").list();
    return rows;
  });

  return (
    <PageLayout title="CloudQuote API Platform">
      <div className="w-full min-h-0 p-0">
        {/* Jumbotron */}
        <section
          className="flex-none m-0 flex justify-center bg-cover bg-center"
          style={{
            background:
              `linear-gradient(90deg, rgb(0 0 0 / 0.5), rgb(0 0 0 / 0.3)), url("/images/bg3.webp") center / cover`,
          }}
        >
          <div className="text-white w-full max-w-240 my-20 px-15 py-10 rounded-2xl backdrop-blur-[2px] bg-white/5">
            <h1 className="border-b-0 text-white text-[3rem] m-0">
              On Demand
              <br/>
              Financial APIs
            </h1>
            <p className="text-white text-base my-[0.8rem]">
              Instant access to millions of datapoints in JSON format
            </p>
            <div className="flex flex-row flex-wrap gap-4">
              <Link className="button browse text-xl py-2 px-4 my-6 me-6" to="/products/api">
                API Directory
              </Link>
              <Link className="button browse text-xl py-2 px-4 my-6 me-6" to="/docs/data-coverage">
                Data Coverage
              </Link>
              <Link className="button browse text-xl py-2 px-4 my-6 me-6" to="/docs/quickstart">
                Quick Start
              </Link>
            </div>
          </div>
        </section>

        {/* Data Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Global Market Data</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Institutional-grade financial data, accessible to every developer
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  icon={<TbWorld size={36} color="#ee6e73" stroke="#ee6e73"/>}
                  title="Real-Time &amp; Accurate"
                  body="Live quotes, historical data, and corporate actions sourced directly from exchanges and premium data providers worldwide"
                />
                <FeatureCard
                  icon={<TbBolt size={36} color="#ee6e73"/>}
                  title="Integrate in Minutes"
                  body="Clean JSON responses, comprehensive docs, and SDKs mean you go from signup to production faster — whether you're building in code, Excel, or WordPress"
                />
                <FeatureCard
                  icon={<TbCurrencyDollar size={36} color="#ee6e73"/>}
                  title="Simple, Transparent Pricing"
                  body="One contract covers everything — equities, FX, crypto, and more. Bundle datasets for volume discounts with no hidden fees"
                />
              </div>
            </div>
            <h3 className="text-[#666] text-[1.6rem] font-normal tracking-[0.08em] mt-10 mb-2 text-center uppercase">
              Trusted Data Sources
            </h3>
            <LogoStrip vendors={dataSources}/>
          </div>
        </section>

        {/* News Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16 bg-[#f9f9fb]">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Financial News at Scale</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Power your platform with a curated, high-velocity news feed via NewsRPM
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  white
                  icon={<TbDatabase size={36} color="#ee6e73"/>}
                  title="100+ Premium Publishers"
                  body="Breaking financial news from the world's most trusted outlets, delivered in real time so your users are always first to know"
                />
                <FeatureCard
                  white
                  icon={<TbBolt size={36} color="#ee6e73"/>}
                  title="Your Own Private Instance"
                  body="A dedicated NewsRPM deployment means zero cross-tenant bleed — you control exactly which topics, tickers, and sources reach your application"
                />
                <FeatureCard
                  white
                  icon={<TbTrendingUp size={36} color="#ee6e73"/>}
                  title="Turn News into Revenue"
                  body="Activate Paid Placements alongside your news feed to create a native advertising channel that generates income without disrupting the user experience"
                />
              </div>
            </div>
            <h3 className="text-[#666] text-[1.6rem] font-normal tracking-[0.08em] mt-10 mb-2 text-center uppercase">
              Content Providers
            </h3>
            <LogoStrip vendors={newsSources}/>
          </div>
        </section>

        {/* AI & Search Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">AI-Native, By Design</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Let an LLM orchestrate every endpoint, plug in your own model, or search by meaning instead of by ticker
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  icon={<TbMessageChatbot size={36} color="#ee6e73"/>}
                  title="Conversational Chat API"
                  body="An OpenAI-compatible /chat endpoint that lets users ask questions in plain English while the model orchestrates quotes, news, fundamentals, and technicals on the fly"
                />
                <FeatureCard
                  icon={<TbPlugConnected size={36} color="#ee6e73"/>}
                  title="Model Context Protocol"
                  body="Every CloudQuote API exposed as an MCP tool — bring your own LLM, run it on-prem, or swap providers without rewriting your integration"
                />
                <FeatureCard
                  icon={<TbSearch size={36} color="#ee6e73"/>}
                  title="Vector &amp; Full-Text Search"
                  body="Search across equities, news, fundamentals, and events with hybrid semantic plus keyword ranking. Find concepts, not just exact tickers"
                />
              </div>
            </div>
            <div className="flex flex-row flex-wrap justify-center gap-4 mt-4">
              <Link className="button browse text-base py-2 px-4" to="/docs/chat-api">
                Chat API Docs
              </Link>
              <Link className="button browse text-base py-2 px-4" to="/docs/mcp">
                MCP Guide
              </Link>
              <Link className="button browse text-base py-2 px-4" to="/docs/agents">
                Building AI Agents
              </Link>
            </div>
          </div>
        </section>

        {/* Integration Paths Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16 bg-[#f9f9fb]">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Built for Every Stack</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Browser, server, mobile, or desktop — pick the integration path that fits your code
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  white
                  icon={<TbCode size={36} color="#ee6e73"/>}
                  title="Front-End Apps"
                  body="Whitelist your domain and call CloudQuote directly from React, Vue, mobile apps, or any browser-side code with full CORS support"
                />
                <FeatureCard
                  white
                  icon={<TbServer size={36} color="#ee6e73"/>}
                  title="Server-Side Apps"
                  body="One private key, every endpoint. Drop CloudQuote into any Node, Python, Java, or .NET back end with predictable rate limits and entitlements"
                />
                <FeatureCard
                  white
                  icon={<TbFileCode size={36} color="#ee6e73"/>}
                  title="Multiple Formats"
                  body="Every data endpoint speaks JSON, CSV, and XML out of the box. Pipe directly into spreadsheets, ETL jobs, or legacy systems with no transformation layer"
                />
              </div>
            </div>
            <div className="flex flex-row flex-wrap justify-center gap-4 mt-4">
              <Link className="button browse text-base py-2 px-4" to="/docs/frontend-integration">
                Front-End Guide
              </Link>
              <Link className="button browse text-base py-2 px-4" to="/docs/backend-integration">
                Back-End Guide
              </Link>
              <Link className="button browse text-base py-2 px-4" to="/docs/authentication">
                Authentication
              </Link>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Powering Every Kind of App</h2>
            <p className="text-[#666] text-base my-0 text-center">
              From retail trading apps to institutional risk engines, the same APIs scale across every use case
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <UseCase
                icon={TbChartCandle}
                title="Trading Platforms"
                body="Real-time quotes, Level 2 order books, and technical indicators for retail brokerages and prop trading desks"
              />
              <UseCase
                icon={TbDashboard}
                title="Research Dashboards"
                body="Combine fundamentals, news, and corporate events into one screen with embeddable widgets or raw JSON"
              />
              <UseCase
                icon={TbDatabase}
                title="Newsrooms &amp; Publishers"
                body="A dedicated NewsRPM instance feeds curated headlines, ticker-tagged content, and paid placements into your CMS"
              />
              <UseCase
                icon={TbRobot}
                title="AI Agents"
                body="Schedule autonomous research, monitoring, and alerting agents that chain search, quotes, and chat into actionable output"
              />
              <UseCase
                icon={TbBuildingBank}
                title="Enterprise Risk"
                body="Enrich proprietary client and counterparty data with public filings, fundamentals, and semantic news monitoring"
              />
              <UseCase
                icon={TbDeviceMobile}
                title="Mobile &amp; Desktop"
                body="Native iOS, Android, and Electron apps integrate with the same endpoints — public keys, OAuth, and short-lived user tokens supported"
              />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="flex-none m-0 px-32 py-20"
          style={{
            background: "linear-gradient(135deg, #1a2942 0%, #041c30 100%)",
          }}
        >
          <div className="max-w-240 mx-auto text-center">
            <h2 className="text-white text-[2.5rem] m-0 mb-3">Ready to build?</h2>
            <p className="text-[#cbd5e1] text-lg m-0 mb-8">
              Sign up to get an API key, or read the docs to see exactly what's possible.
            </p>
            <div className="flex flex-row flex-wrap justify-center gap-4">
              <Link className="button browse text-xl py-2 px-6 my-2" to="/signup">
                Get an API Key
              </Link>
              <Link className="button browse text-xl py-2 px-6 my-2" to="/docs">
                Read the Docs
              </Link>
              <Link className="button browse text-xl py-2 px-6 my-2" to="/contact">
                Talk to Sales
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}

function FeatureCard({
                       icon,
                       title,
                       body,
                       white = false,
                     }: {
  icon: ReactNode;
  title: string;
  body: string;
  white?: boolean;
}) {
  return (
    <div
      className={`${white ? "bg-white" : "bg-white"} border border-[#eee] rounded-lg shadow-[0_2px_8px_rgb(0_0_0/0.04)] flex-[0_1_33%] p-8 px-6 text-center`}
    >
      <div className="mb-5 mx-auto w-fit">{icon}</div>
      <h3 className="text-[1.3rem] leading-[1.2] mt-0 mb-3 text-center">{title}</h3>
      <p className="text-[#555] text-[1rem] leading-[1.6] m-0">{body}</p>
    </div>
  );
}

function UseCase({ icon: Icon, title, body }: { icon: ComponentType<{ size?: number; color?: string }>; title: string; body: string }) {
  return (
    <div className="bg-white border border-[#eee] rounded-lg p-6 hover:shadow-[0_4px_12px_rgb(0_0_0/0.06)] transition-shadow">
      <div className="mb-3">
        <Icon size={28} color="#ee6e73"/>
      </div>
      <h3 className="text-lg font-semibold m-0 mb-2 text-[#1a1a2e]">{title}</h3>
      <p className="text-[#555] text-sm leading-relaxed m-0">{body}</p>
    </div>
  );
}

function LogoStrip({ vendors }: { vendors: Array<{ website?: string | null | undefined, image?: string | null | undefined }> | null | undefined }) {
  return (
    <div className="flex flex-row flex-wrap gap-12 items-center justify-center border-t border-[#eee] mt-6 pt-8">
      {vendors?.map((vendor, i) =>
        vendor.image ? (
          <a key={i} className="flex-none" target="_blank" href={vendor.website!} rel="noreferrer">
            <img
              src={vendor.image}
              className="h-[1.7em] cursor-pointer grayscale opacity-45 transition-[filter,opacity] duration-300 hover:grayscale-0 hover:opacity-100"
            />
          </a>
        ) : null,
      )}
    </div>
  );
}
